import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Cloud, FolderPlus, Folder, Upload, Trash2, Share2, Download,
  Image as ImageIcon, Film, FileText, File as FileIcon, X, Loader2,
  ArrowLeft, Copy, Eye, Edit2, ChevronRight, List, LayoutGrid
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;
const PUBLIC_BASE = window.location.origin;

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b > 1024 * 1024 * 1024) return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
  if (b > 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
  if (b > 1024) return (b / 1024).toFixed(0) + ' KB';
  return b + ' B';
};
const formatEta = (sec) => {
  if (sec === null || sec === undefined) return '';
  if (sec < 60) return `~${sec}sn`;
  const m = Math.floor(sec / 60);
  return `~${m}dk ${sec % 60}sn`;
};

const kindIcon = (k) => {
  if (k === 'image') return ImageIcon;
  if (k === 'video') return Film;
  if (k === 'pdf') return FileText;
  return FileIcon;
};

export default function HesapCloudPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null); // null = list view, obj = inside folder
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renaming, setRenaming] = useState(null); // { ...item, _type: 'folder' | 'file' }
  const [renameValue, setRenameValue] = useState('');

  // Upload progress + xhr ref for abort
  const [uploads, setUploads] = useState([]);
  const uploadXhrsRef = useRef({});

  // Download progress
  const [downloads, setDownloads] = useState([]);
  const downloadXhrsRef = useRef({});

  // Preview & share
  const [preview, setPreview] = useState(null);

  // View mode & sort (persisted)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('cloud-view-mode') || 'details');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('cloud-sort') || 'name-asc');
  useEffect(() => { localStorage.setItem('cloud-view-mode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('cloud-sort', sortBy); }, [sortBy]);

  const sortItems = (arr) => {
    const a = [...arr];
    a.sort((x, y) => {
      if (sortBy === 'name-asc') return (x.name || '').localeCompare(y.name || '', 'tr');
      if (sortBy === 'name-desc') return (y.name || '').localeCompare(x.name || '', 'tr');
      if (sortBy === 'date-asc') return (x.created_at || '').localeCompare(y.created_at || '');
      return (y.created_at || '').localeCompare(x.created_at || '');
    });
    return a;
  };

  const sortedFolders = useMemo(() => sortItems(folders), [folders, sortBy]);
  const sortedFiles = useMemo(() => sortItems(files), [files, sortBy]);

  // Preview gallery navigation (sortedFiles tanımlandıktan sonra olmalı)
  const previewIdx = useMemo(() => preview ? sortedFiles.findIndex(x => x.id === preview.id) : -1, [preview, sortedFiles]);
  const previewGoPrev = () => {
    if (previewIdx > 0) setPreview(sortedFiles[previewIdx - 1]);
  };
  const previewGoNext = () => {
    if (previewIdx >= 0 && previewIdx < sortedFiles.length - 1) setPreview(sortedFiles[previewIdx + 1]);
  };
  // Touch swipe state
  const previewTouchStart = useRef(null);
  const onPreviewTouchStart = (e) => { previewTouchStart.current = e.touches[0].clientX; };
  const onPreviewTouchEnd = (e) => {
    if (previewTouchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - previewTouchStart.current;
    previewTouchStart.current = null;
    if (delta <= -60) previewGoNext();
    else if (delta >= 60) previewGoPrev();
  };
  const previewDelete = async () => {
    if (!preview) return;
    if (!window.confirm(`"${preview.name}" dosyasını silmek istediğinize emin misiniz?`)) return;
    try {
      await fetch(`${API}/cloud/files/${preview.id}`, { method: 'DELETE', headers });
      toast.success('Dosya silindi');
      // ID bazlı, çünkü sortedFiles henüz yenilenmedi
      const remaining = sortedFiles.filter(f => f.id !== preview.id);
      const nextItem = remaining[previewIdx] || remaining[previewIdx - 1] || null;
      setPreview(nextItem);
      loadFiles(currentFolder.id);
      loadFolders();
    } catch { toast.error('Hata'); }
  };
  // Klavye: ok tuşları + Delete
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') previewGoPrev();
      else if (e.key === 'ArrowRight') previewGoNext();
      else if (e.key === 'Delete') previewDelete();
      else if (e.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, previewIdx, sortedFiles]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const [shareDialog, setShareDialog] = useState(null); // {kind, target}
  const [shareId, setShareId] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareAllowUpload, setShareAllowUpload] = useState(false);
  const [shareUploadEnabled, setShareUploadEnabled] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);

  const loadFolders = async () => {
    try {
      const r = await fetch(`${API}/cloud/folders`, { headers });
      setFolders(await r.json());
    } catch { toast.error('Klasörler yüklenemedi'); }
  };

  const loadFiles = async (folderId) => {
    try {
      const r = await fetch(`${API}/cloud/files?folder_id=${folderId || ''}`, { headers });
      setFiles(await r.json());
    } catch { toast.error('Dosyalar yüklenemedi'); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadFolders();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (currentFolder) loadFiles(currentFolder.id);
    else setFiles([]);
  }, [currentFolder]);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const r = await fetch(`${API}/cloud/folders`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      if (!r.ok) throw new Error();
      toast.success('Klasör oluşturuldu');
      setShowNewFolder(false);
      setNewFolderName('');
      loadFolders();
    } catch { toast.error('Klasör oluşturulamadı'); }
  };

  const renameItem = async () => {
    if (!renaming || !renameValue.trim()) return;
    const isFile = renaming._type === 'file';
    const endpoint = isFile ? `${API}/cloud/files/${renaming.id}` : `${API}/cloud/folders/${renaming.id}`;
    try {
      const r = await fetch(endpoint, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renameValue.trim() })
      });
      if (!r.ok) throw new Error('rename failed');
      toast.success('Yeniden adlandırıldı');
      setRenaming(null);
      if (isFile) {
        loadFiles(currentFolder?.id);
      } else {
        loadFolders();
        if (currentFolder?.id === renaming.id) {
          setCurrentFolder({ ...currentFolder, name: renameValue.trim() });
        }
      }
    } catch { toast.error('Hata'); }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`"${folder.name}" klasörünü ve içindeki tüm dosyaları silmek istediğinize emin misiniz?`)) return;
    try {
      await fetch(`${API}/cloud/folders/${folder.id}`, { method: 'DELETE', headers });
      toast.success('Klasör silindi');
      loadFolders();
      if (currentFolder?.id === folder.id) setCurrentFolder(null);
    } catch { toast.error('Hata'); }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`"${f.name}" dosyasını silmek istediğinize emin misiniz?`)) return;
    try {
      await fetch(`${API}/cloud/files/${f.id}`, { method: 'DELETE', headers });
      toast.success('Dosya silindi');
      loadFiles(currentFolder.id);
      loadFolders(); // count güncelle
    } catch { toast.error('Hata'); }
  };

  // Upload via XHR with progress
  const handleFiles = async (e) => {
    if (!currentFolder) {
      toast.error('Önce bir klasöre girin veya oluşturun');
      return;
    }
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of list) {
      // Aynı isimde dosya var mı kontrol et (boyut da kontrol edilir)
      try {
        const params = new URLSearchParams({ name: f.name, folder_id: currentFolder.id, size: String(f.size) });
        const r = await fetch(`${API}/cloud/files/exists?${params}`, { headers });
        if (r.ok) {
          const data = await r.json();
          if (data.exists) {
            const msg = data.same_size
              ? `"${f.name}" adında VE aynı boyutta (${formatBytes(f.size)}) bir dosya zaten var.\n\nYine de yüklensin mi?`
              : `"${f.name}" adında bir dosya zaten var.\n\nYine de yüklensin mi? (Aynı isimde iki dosya bulunacak)`;
            const ok = window.confirm(msg);
            if (!ok) continue;
          }
        }
      } catch {
        // Kontrol başarısız olursa yine de yüklemeye izin ver
      }
      uploadFile(f);
    }
  };

  const uploadFile = (file) => {
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder_id', currentFolder.id);

    const xhr = new XMLHttpRequest();
    uploadXhrsRef.current[uid] = xhr;
    xhr.open('POST', `${API}/cloud/files`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    const startedAt = Date.now();

    setUploads(prev => [...prev, { id: uid, name: file.name, pct: 0, loaded: 0, total: file.size, speed: 0, eta: null, status: 'uploading' }]);

    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      const pct = Math.round((ev.loaded / ev.total) * 100);
      const elapsed = (Date.now() - startedAt) / 1000;
      let speed = 0, eta = null;
      if (elapsed > 0.3) {
        speed = ev.loaded / elapsed;
        if (speed > 0) eta = Math.max(0, Math.round((ev.total - ev.loaded) / speed));
      }
      setUploads(prev => prev.map(u => u.id === uid ? { ...u, pct, loaded: ev.loaded, total: ev.total, speed, eta } : u));
    };
    xhr.onload = () => {
      delete uploadXhrsRef.current[uid];
      if (xhr.status >= 200 && xhr.status < 300) {
        loadFiles(currentFolder.id);
        loadFolders();
        setUploads(prev => prev.map(u => u.id === uid ? { ...u, status: 'done', pct: 100 } : u));
      } else {
        toast.error(`${file.name} yüklenemedi (HTTP ${xhr.status})`);
        setUploads(prev => prev.map(u => u.id === uid ? { ...u, status: 'error' } : u));
      }
      setTimeout(() => setUploads(prev => prev.filter(u => u.id !== uid)), 2500);
    };
    xhr.onerror = () => {
      delete uploadXhrsRef.current[uid];
      toast.error(`${file.name} yüklenemedi`);
      setUploads(prev => prev.filter(u => u.id !== uid));
    };
    xhr.onabort = () => {
      delete uploadXhrsRef.current[uid];
      setUploads(prev => prev.filter(u => u.id !== uid));
    };
    xhr.send(fd);
  };

  const cancelUpload = (uid) => {
    const xhr = uploadXhrsRef.current[uid];
    if (xhr) {
      xhr.abort();
      toast.info('Yükleme iptal edildi');
    }
  };

  const downloadFile = (f) => {
    // Tarayıcının indirme yöneticisine devret — bellekte tutmadan diske akar.
    const url = `${API}/cloud/files/${f.id}/download?t=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1000);
    toast.success('İndirme başlatıldı — tarayıcının indirme bölümünden takip edebilirsiniz');
  };

  const cancelDownload = () => { /* native indirme; iptal yok */ };

  const createShare = async (opts) => {
    setCreatingShare(true);
    try {
      const r = await fetch(`${API}/cloud/share`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(opts)
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      const url = `${PUBLIC_BASE}/bulut/paylasim/${d.token}`;
      setShareUrl(url);
      setShareId(d.id);
      setShareUploadEnabled(!!d.allow_upload);
      setShareAllowUpload(!!d.allow_upload);
    } catch { toast.error('Link oluşturulamadı'); }
    finally { setCreatingShare(false); }
  };

  // Mevcut share'i bul veya yenisini oluştur — token değişmesin
  const openOrCreateShare = async (kind, target) => {
    setShareDialog({ kind, target });
    setShareUrl('');
    setShareId(null);
    setShareAllowUpload(false);
    setShareUploadEnabled(false);
    setCreatingShare(true);
    try {
      const q = kind === 'file' ? `file_id=${target.id}` : `folder_id=${target.id}`;
      const r = await fetch(`${API}/cloud/share/by-target?${q}`, { headers });
      const d = await r.json();
      if (d.exists) {
        setShareUrl(`${PUBLIC_BASE}/bulut/paylasim/${d.token}`);
        setShareId(d.id);
        setShareAllowUpload(d.allow_upload);
        setShareUploadEnabled(d.allow_upload);
        setCreatingShare(false);
      } else {
        await createShare(kind === 'file' ? { file_id: target.id } : { folder_id: target.id });
      }
    } catch {
      setCreatingShare(false);
      toast.error('Link alınamadı');
    }
  };

  const openShareForFile = (f) => openOrCreateShare('file', f);
  const openShareForFolder = (folder) => openOrCreateShare('folder', folder);

  // Sadece allow_upload bayrağını değiştir — token AYNI kalır
  const regenShareWithUpload = async (enabled) => {
    if (!shareDialog || shareDialog.kind !== 'folder' || !shareId) return;
    setShareAllowUpload(enabled);
    try {
      const r = await fetch(`${API}/cloud/share/${shareId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_upload: enabled })
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setShareUploadEnabled(!!d.allow_upload);
      toast.success(d.allow_upload ? 'Yükleme izni açıldı' : 'Yükleme izni kapatıldı');
    } catch {
      toast.error('Güncellenemedi');
      setShareAllowUpload(!enabled);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link panoya kopyalandı');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed'; ta.style.top = '-9999px';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Link panoya kopyalandı');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  // ============== FOLDER LIST VIEW ==============
  if (!currentFolder) {
    return (
      <div className="space-y-4" data-testid="cloud-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Cloud className="h-7 w-7 text-sky-500" />
              Bulut Depolama
            </h1>
            <p className="text-sm text-slate-500">Klasör oluştur, dosya yükle, link paylaş</p>
          </div>
          <Button onClick={() => setShowNewFolder(true)} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="new-folder-btn">
            <FolderPlus className="h-4 w-4 mr-2" />Yeni Klasör
          </Button>
        </div>

        {folders.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <Cloud className="h-16 w-16 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 mb-3">Henüz klasör yok</p>
            <Button onClick={() => setShowNewFolder(true)} className="bg-sky-600 hover:bg-sky-700">
              <FolderPlus className="h-4 w-4 mr-2" />İlk klasörü oluştur
            </Button>
          </CardContent></Card>
        ) : (
          <>
            <ViewToolbar viewMode={viewMode} setViewMode={setViewMode}
              sortBy={sortBy} setSortBy={setSortBy} count={folders.length} unit="klasör" />
            {viewMode === 'details' ? (
              <Card>
                <div className="divide-y" data-testid="folders-list">
                  {sortedFolders.map(f => (
                    <div key={f.id} className="flex items-center gap-3 px-3 py-2 hover:bg-sky-50 transition group"
                      data-testid={`folder-${f.id}`}>
                      <Folder className="h-7 w-7 text-sky-500 flex-shrink-0 cursor-pointer" onClick={() => setCurrentFolder(f)} />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setCurrentFolder(f)}>
                        <p className="font-semibold truncate text-sm">{f.name}</p>
                        <p className="text-[11px] text-slate-500">
                          {f.file_count || 0} dosya · {formatBytes(f.total_size || 0)}
                          {f.created_at && ` · ${formatDate(f.created_at)}`}
                        </p>
                      </div>
                      <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition">
                        <button onClick={() => { setRenaming({ ...f, _type: 'folder' }); setRenameValue(f.name); }} title="Yeniden adlandır" className="p-1.5 hover:bg-slate-100 rounded">
                          <Edit2 className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={() => openShareForFolder(f)} title="Klasörü paylaş" className="p-1.5 hover:bg-sky-100 rounded">
                          <Share2 className="h-3.5 w-3.5 text-sky-600" />
                        </button>
                        <button onClick={() => deleteFolder(f)} title="Sil" className="p-1.5 hover:bg-red-100 rounded">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {sortedFolders.map(f => (
                  <Card key={f.id}
                    className="hover:shadow-md transition cursor-pointer group"
                    data-testid={`folder-${f.id}`}>
                    <CardContent className="p-4">
                      <div onClick={() => setCurrentFolder(f)}>
                        <Folder className="h-12 w-12 text-sky-500 mb-2" />
                        <p className="font-bold truncate">{f.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {f.file_count || 0} dosya · {formatBytes(f.total_size || 0)}
                        </p>
                      </div>
                      <div className="flex justify-end gap-1 mt-2 opacity-0 group-hover:opacity-100 transition">
                        <button onClick={() => { setRenaming({ ...f, _type: 'folder' }); setRenameValue(f.name); }}
                          title="Yeniden adlandır"
                          className="p-1.5 hover:bg-slate-100 rounded">
                          <Edit2 className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={() => openShareForFolder(f)}
                          title="Klasörü paylaş"
                          className="p-1.5 hover:bg-sky-100 rounded">
                          <Share2 className="h-3.5 w-3.5 text-sky-600" />
                        </button>
                        <button onClick={() => deleteFolder(f)}
                          title="Sil"
                          className="p-1.5 hover:bg-red-100 rounded">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* New folder dialog */}
        <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Yeni Klasör</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createFolder()}
                placeholder="Örn: Müşteri Belgeleri"
                autoFocus data-testid="new-folder-name-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewFolder(false)}>İptal</Button>
                <Button onClick={createFolder} className="bg-sky-600 hover:bg-sky-700" data-testid="create-folder-btn">
                  <FolderPlus className="h-4 w-4 mr-2" />Oluştur
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename dialog */}
        <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{renaming?._type === 'file' ? 'Dosyayı Yeniden Adlandır' : 'Klasörü Yeniden Adlandır'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && renameItem()} autoFocus />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRenaming(null)}>İptal</Button>
                <Button onClick={renameItem} className="bg-sky-600 hover:bg-sky-700">Kaydet</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ShareDialog dialog={shareDialog} url={shareUrl} loading={creatingShare}
          allowUpload={shareAllowUpload}
          uploadEnabled={shareUploadEnabled}
          onUploadToggle={regenShareWithUpload}
          onClose={() => { setShareDialog(null); setShareUrl(''); setShareAllowUpload(false); }}
          onCopy={copyToClipboard} />

        <ProgressWidget uploads={uploads} downloads={downloads}
          onCancelUpload={cancelUpload} onCancelDownload={cancelDownload} />
      </div>
    );
  }

  // ============== INSIDE FOLDER VIEW ==============
  return (
    <div className="space-y-4" data-testid="cloud-folder-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setCurrentFolder(null)}>
            <ArrowLeft className="h-4 w-4 mr-1" />Geri
          </Button>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Cloud className="h-4 w-4" />
            <span>Bulut</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Folder className="h-5 w-5 text-sky-500" />
            {currentFolder.name}
          </h1>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
            {files.length} dosya · {formatBytes(files.reduce((s, f) => s + (f.size || 0), 0))}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" multiple ref={fileInputRef} onChange={handleFiles} className="hidden" data-testid="file-input" />
          <Button onClick={() => fileInputRef.current?.click()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="upload-btn">
            <Upload className="h-4 w-4 mr-2" />Dosya Yükle
          </Button>
          <Button onClick={() => openShareForFolder(currentFolder)}
            variant="outline" className="border-sky-200 text-sky-600 hover:bg-sky-50" data-testid="share-folder-btn">
            <Share2 className="h-4 w-4 mr-2" />Klasörü Paylaş
          </Button>
        </div>
      </div>

      {/* Files grid */}
      {files.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Upload className="h-16 w-16 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 mb-3">Henüz dosya yok</p>
          <Button onClick={() => fileInputRef.current?.click()} className="bg-emerald-600 hover:bg-emerald-700">
            <Upload className="h-4 w-4 mr-2" />Dosya Yükle
          </Button>
        </CardContent></Card>
      ) : (
        <>
          <ViewToolbar viewMode={viewMode} setViewMode={setViewMode}
            sortBy={sortBy} setSortBy={setSortBy} count={files.length} unit="dosya" />
          {viewMode === 'details' ? (
            <Card>
              <div className="divide-y" data-testid="files-list">
                {sortedFiles.map(f => {
                  const Ic = kindIcon(f.kind);
                  return (
                    <div key={f.id} className="flex items-center gap-3 px-3 py-2 hover:bg-emerald-50 transition group"
                      data-testid={`file-${f.id}`}>
                      <Ic className={`h-6 w-6 flex-shrink-0 cursor-pointer ${
                        f.kind === 'image' ? 'text-pink-500'
                        : f.kind === 'video' ? 'text-purple-500'
                        : f.kind === 'pdf' ? 'text-red-500'
                        : 'text-slate-400'
                      }`} onClick={() => setPreview(f)} />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPreview(f)}>
                        <p className="font-medium truncate text-sm">{f.name}</p>
                        <p className="text-[11px] text-slate-500">
                          {formatBytes(f.size)}{f.created_at && ` · ${formatDate(f.created_at)}`}
                        </p>
                      </div>
                      <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition">
                        <button onClick={() => setPreview(f)} title="Önizle" className="p-1.5 hover:bg-slate-100 rounded">
                          <Eye className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={() => { setRenaming({ ...f, _type: 'file' }); setRenameValue(f.name); }}
                          title="Yeniden adlandır" className="p-1.5 hover:bg-slate-100 rounded"
                          data-testid={`rename-file-${f.id}`}>
                          <Edit2 className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={() => downloadFile(f)} title="İndir"
                          className="p-1.5 hover:bg-emerald-100 rounded" data-testid={`download-file-${f.id}`}>
                          <Download className="h-3.5 w-3.5 text-emerald-600" />
                        </button>
                        <button onClick={() => openShareForFile(f)} title="Paylaş"
                          className="p-1.5 hover:bg-sky-100 rounded" data-testid={`share-file-${f.id}`}>
                          <Share2 className="h-3.5 w-3.5 text-sky-600" />
                        </button>
                        <button onClick={() => deleteFile(f)} title="Sil" className="p-1.5 hover:bg-red-100 rounded">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {sortedFiles.map(f => {
                const Ic = kindIcon(f.kind);
                return (
                  <Card key={f.id} className="hover:shadow-md transition group overflow-hidden" data-testid={`file-${f.id}`}>
                    <div className="aspect-square bg-slate-100 relative flex items-center justify-center cursor-pointer"
                      onClick={() => setPreview(f)}>
                      {f.kind === 'image' ? (
                        <img src={`${API}/cloud/files/${f.id}/raw?t=${encodeURIComponent(token)}`}
                          alt={f.name}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          className="w-full h-full object-cover" />
                      ) : (
                        <Ic className="h-16 w-16 text-slate-400" />
                      )}
                      {f.kind === 'video' && (
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <Film className="h-12 w-12 text-white" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-2">
                      <p className="text-xs font-medium truncate">{f.name}</p>
                      <p className="text-[10px] text-slate-500">{formatBytes(f.size)}</p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <button onClick={() => setPreview(f)} title="Önizle"
                          className="flex-1 p-1.5 hover:bg-slate-100 rounded flex items-center justify-center">
                          <Eye className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={() => { setRenaming({ ...f, _type: 'file' }); setRenameValue(f.name); }}
                          title="Yeniden adlandır"
                          className="flex-1 p-1.5 hover:bg-slate-100 rounded flex items-center justify-center"
                          data-testid={`rename-file-${f.id}`}>
                          <Edit2 className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={() => downloadFile(f)} title="İndir"
                          className="flex-1 p-1.5 hover:bg-emerald-100 rounded flex items-center justify-center"
                          data-testid={`download-file-${f.id}`}>
                          <Download className="h-3.5 w-3.5 text-emerald-600" />
                        </button>
                        <button onClick={() => openShareForFile(f)} title="Paylaş"
                          className="flex-1 p-1.5 hover:bg-sky-100 rounded flex items-center justify-center"
                          data-testid={`share-file-${f.id}`}>
                          <Share2 className="h-3.5 w-3.5 text-sky-600" />
                        </button>
                        <button onClick={() => deleteFile(f)} title="Sil"
                          className="flex-1 p-1.5 hover:bg-red-100 rounded flex items-center justify-center">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              {preview && (() => {
                const Ic = kindIcon(preview.kind);
                return <Ic className="h-5 w-5" />;
              })()}
              <span className="truncate flex-1">{preview?.name}</span>
              {preview && sortedFiles.length > 1 && (
                <span className="text-xs font-normal text-slate-500 flex-shrink-0">
                  {previewIdx + 1} / {sortedFiles.length}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-2">
              <div
                className="bg-slate-50 rounded-lg p-2 flex items-center justify-center min-h-[200px] relative select-none"
                onTouchStart={onPreviewTouchStart}
                onTouchEnd={onPreviewTouchEnd}
                style={{ touchAction: 'pan-y' }}
              >
                {/* Sol ok */}
                {sortedFiles.length > 1 && (
                  <button
                    onClick={previewGoPrev}
                    disabled={previewIdx <= 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow disabled:opacity-30"
                    data-testid="preview-prev-btn"
                    aria-label="Önceki"
                  >
                    <ArrowLeft className="h-5 w-5 text-slate-700" />
                  </button>
                )}

                {preview.kind === 'image' && (
                  <img src={`${API}/cloud/files/${preview.id}/raw?t=${encodeURIComponent(token)}`} alt={preview.name}
                    className="max-w-full max-h-[60vh] rounded pointer-events-none" draggable={false} />
                )}
                {preview.kind === 'video' && (
                  <video src={`${API}/cloud/files/${preview.id}/raw?t=${encodeURIComponent(token)}`} controls
                    className="max-w-full max-h-[60vh] rounded" />
                )}
                {preview.kind === 'audio' && (
                  <audio src={`${API}/cloud/files/${preview.id}/raw?t=${encodeURIComponent(token)}`} controls />
                )}
                {(preview.kind === 'pdf' || preview.kind === 'file') && (
                  <div className="text-center p-8">
                    <FileText className="h-16 w-16 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">{preview.name}</p>
                    <p className="text-xs text-slate-400">{formatBytes(preview.size)}</p>
                  </div>
                )}

                {/* Sağ ok */}
                {sortedFiles.length > 1 && (
                  <button
                    onClick={previewGoNext}
                    disabled={previewIdx >= sortedFiles.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow disabled:opacity-30"
                    data-testid="preview-next-btn"
                    aria-label="Sonraki"
                  >
                    <ChevronRight className="h-5 w-5 text-slate-700" />
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-400 text-center hidden sm:block">
                Klavye: ← → ile gezin · Delete ile sil · Esc ile kapat
              </p>
              <div className="flex justify-end gap-2 flex-wrap">
                <Button onClick={previewDelete} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="preview-delete-btn">
                  <Trash2 className="h-4 w-4 mr-2" />Sil
                </Button>
                <Button onClick={() => { setRenaming({ ...preview, _type: 'file' }); setRenameValue(preview.name); }}
                  variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                  <Edit2 className="h-4 w-4 mr-2" />Yeniden Adlandır
                </Button>
                <Button onClick={() => downloadFile(preview)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="h-4 w-4 mr-2" />İndir
                </Button>
                <Button onClick={() => openShareForFile(preview)} variant="outline" className="border-sky-200 text-sky-600 hover:bg-sky-50">
                  <Share2 className="h-4 w-4 mr-2" />Paylaş
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ShareDialog dialog={shareDialog} url={shareUrl} loading={creatingShare}
        allowUpload={shareAllowUpload}
        uploadEnabled={shareUploadEnabled}
        onUploadToggle={regenShareWithUpload}
        onClose={() => { setShareDialog(null); setShareUrl(''); setShareAllowUpload(false); }}
        onCopy={copyToClipboard} />

      <ProgressWidget uploads={uploads} downloads={downloads}
        onCancelUpload={cancelUpload} onCancelDownload={cancelDownload} />
    </div>
  );
}

function ViewToolbar({ viewMode, setViewMode, sortBy, setSortBy, count, unit }) {
  return (
    <div className="flex items-center justify-between gap-2 bg-white border rounded-lg px-3 py-2" data-testid="view-toolbar">
      <div className="text-xs text-slate-500">
        <b>{count}</b> {unit}
      </div>
      <div className="flex items-center gap-2">
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="text-xs border rounded px-2 py-1 bg-white cursor-pointer hover:border-sky-400 focus:outline-none focus:border-sky-500"
          data-testid="cloud-sort-select">
          <option value="date-desc">Tarih (Yeni → Eski)</option>
          <option value="date-asc">Tarih (Eski → Yeni)</option>
          <option value="name-asc">İsim (A → Z)</option>
          <option value="name-desc">İsim (Z → A)</option>
        </select>
        <div className="flex border rounded overflow-hidden">
          <button onClick={() => setViewMode('details')}
            title="Detay görünümü (hızlı, resim yüklenmez)"
            className={`p-1.5 transition ${viewMode === 'details' ? 'bg-sky-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
            data-testid="view-details-btn">
            <List className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('thumbs')}
            title="Küçük resim görünümü"
            className={`p-1.5 transition ${viewMode === 'thumbs' ? 'bg-sky-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
            data-testid="view-thumbs-btn">
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgressWidget({ uploads, downloads, onCancelUpload, onCancelDownload }) {
  const items = [
    ...uploads.map(u => ({ ...u, type: 'upload' })),
    ...downloads.map(d => ({ ...d, type: 'download' }))
  ];
  if (items.length === 0) return null;

  const formatBytes2 = (b) => {
    if (!b) return '0 B';
    if (b > 1024 * 1024 * 1024) return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
    if (b > 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    if (b > 1024) return (b / 1024).toFixed(0) + ' KB';
    return b + ' B';
  };
  const formatEta2 = (sec) => {
    if (sec === null || sec === undefined) return '';
    if (sec < 60) return `~${sec}sn`;
    const m = Math.floor(sec / 60);
    return `~${m}dk ${sec % 60}sn`;
  };

  return (
    <div className="fixed bottom-4 right-4 w-[320px] max-w-[90vw] bg-white rounded-xl shadow-2xl border-2 border-slate-200 z-50 overflow-hidden"
      data-testid="progress-widget">
      <div className="bg-gradient-to-r from-emerald-600 to-sky-600 text-white px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold">
          <Upload className="h-3.5 w-3.5" />
          {items.length} işlem
        </div>
      </div>
      <div className="max-h-[320px] overflow-y-auto p-2 space-y-2">
        {items.map(it => {
          const isUp = it.type === 'upload';
          const isPreparing = it.status === 'preparing';
          const isDone = it.status === 'done';
          const isError = it.status === 'error';
          const accent = isUp ? 'emerald' : 'sky';
          return (
            <div key={`${it.type}-${it.id}`} className="bg-slate-50 rounded-lg p-2 space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs">
                {isUp ? <Upload className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                      : <Download className="h-3 w-3 text-sky-600 flex-shrink-0" />}
                <span className="truncate flex-1 font-medium">{it.name}</span>
                {!isDone && !isError && (
                  <button onClick={() => isUp ? onCancelUpload(it.id) : onCancelDownload(it.id)}
                    title="İptal"
                    className="p-0.5 hover:bg-red-100 rounded text-red-500 flex-shrink-0"
                    data-testid={`cancel-${it.type}-${it.id}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-600">
                <span>
                  {isPreparing && 'Hazırlanıyor...'}
                  {isDone && 'Tamamlandı'}
                  {isError && 'Hata'}
                  {!isPreparing && !isDone && !isError && `${it.pct}% · ${formatBytes2(it.loaded)} / ${formatBytes2(it.total)}`}
                </span>
                {!isDone && !isError && it.speed > 0 && (
                  <span>{formatBytes2(it.speed)}/sn · {formatEta2(it.eta)}</span>
                )}
              </div>
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full bg-gradient-to-r transition-all ${
                  isError ? 'from-red-500 to-red-600'
                  : isDone ? 'from-emerald-500 to-emerald-600 w-full'
                  : isPreparing ? `from-${accent}-400 to-${accent}-500 animate-pulse w-1/3`
                  : `from-${accent}-500 to-${accent}-600`
                }`}
                  style={isPreparing || isDone ? {} : { width: `${it.pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShareDialog({ dialog, url, loading, allowUpload, uploadEnabled, onUploadToggle, onClose, onCopy }) {
  if (!dialog) return null;
  const isFolder = dialog.kind === 'folder';
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-sky-500" />
            {isFolder ? 'Klasör Paylaşımı' : 'Dosya Paylaşımı'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded p-2 text-sm">
            <b>{isFolder ? 'Klasör:' : 'Dosya:'}</b>{' '}
            {dialog.target?.name}
          </div>

          {isFolder && (
            <label className="flex items-start gap-2 p-3 bg-amber-50/50 border-2 border-dashed border-amber-200 rounded-lg cursor-pointer hover:border-amber-300 transition">
              <input type="checkbox"
                checked={allowUpload}
                onChange={(e) => onUploadToggle(e.target.checked)}
                className="mt-1 w-4 h-4 accent-amber-600"
                data-testid="share-allow-upload-checkbox" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-900 flex items-center gap-1">
                  <Upload className="h-4 w-4" />
                  Yükleme İzni Ver
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Bu seçenek aktif olursa, link sahibi dosya yükleyebilir.
                  Yüklediği dosyaları sizin panelinizde görür ve indirebilirsiniz.
                </p>
              </div>
            </label>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Link hazırlanıyor...
            </div>
          ) : url ? (
            <>
              <div>
                <p className="text-xs text-slate-500 mb-1">
                  {uploadEnabled ? 'Yükleme + İndirme linki' : 'Sadece görüntüleme/indirme linki'}:
                </p>
                <div className="flex gap-2">
                  <Input value={url} readOnly className="font-mono text-xs" data-testid="share-url-input" />
                  <Button onClick={() => onCopy(url)} className="bg-sky-600 hover:bg-sky-700" data-testid="copy-share-link-btn">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className={`border rounded p-2 text-xs ${uploadEnabled ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                {uploadEnabled
                  ? '✓ Yükleme izni aktif — link sahibi dosya yükleyebilir. Yüklediği dosyaları sizin klasörünüzde görürsünüz.'
                  : 'Bu linki olan herkes dosyaya erişebilir ve indirebilir. Linki güvenli paylaşın.'}
              </div>
              <div className="flex justify-end">
                <Button onClick={onClose} variant="outline">Kapat</Button>
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
